import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  forwardRef,
  MouseEventHandler,
  ReactNode,
} from 'react';
import Button from './Button';
import Box from '@o2/ui/Box';
import Stack from '@o2/ui/Stack';
import Typography from '@o2/ui/Typography';
import ChevronDown from '@o2/icons/ChevronDown';
import PersonFill from '@o2/icons/PersonFill';
import { appBarInternalElmIds } from './AppBarInternal';
import colors from '@o2/ui/theme/colors';

export type UserButtonProps<T extends ElementType = 'button'> = {
  /**
   * Text or Component in button
   */
  children: ReactNode;

  /**
   * If false, an extra icon will be displayed.
   */
  hasContactPerson?: boolean;

  /**
   * If not undefined, the verified text is displayed
   */
  verified?: boolean;

  /**
   * Verified status text message
   */
  verifiedStatus?: ReactNode;

  /**
   * If not undefined, the chevron icon is displayed
   */
  open?: boolean;

  onClick?: MouseEventHandler;

  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;

  ref?: ComponentPropsWithRef<T>['ref'];
} & ComponentPropsWithoutRef<T>;

const UserButton = forwardRef(
  <T extends ElementType = 'button'>(
    { children, hasContactPerson, verified, verifiedStatus, open, onClick, ...props }: UserButtonProps<T>,
    ref: UserButtonProps<T>['ref']
  ) => {
    return (
      <Button onClick={onClick} {...props} ref={ref}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            data-elm={
              hasContactPerson ? appBarInternalElmIds.contactPersonBtn : appBarInternalElmIds.selectContactPersonBtn
            }
          >
            {children}
          </Box>
          {hasContactPerson && verified !== undefined && (
            <Typography
              data-elm={appBarInternalElmIds.userButtonVerifiedStatus}
              component="span"
              color={verified ? colors.success[400] : colors.warning[400]}
            >
              {verifiedStatus}
            </Typography>
          )}
          {(!hasContactPerson || verified === undefined) && <PersonFill />}
          {open !== undefined && (
            <ChevronDown
              sx={{ fontSize: 14, transition: 'transform 100ms', transform: `rotate(${open ? '180' : '0'}deg)` }}
            />
          )}
        </Stack>
      </Button>
    );
  }
);

UserButton.displayName = 'AppBarInternal.UserButton';

export default UserButton;
